import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Button, Field, Input, Notice } from '@/components/ui';
import { useI18n } from '@/i18n';
import { ApiError } from '@/lib/api';
import { errorText } from '@/lib/errors';
import { PASSWORD_RULES, useChangePassword, useSession, useSignIn } from '@/lib/auth';

/**
 * Two states: `signin`, and `change` for a staff member's first login with the temporary
 * password. Layout and copy follow the approved login design from the brand handoff.
 */
export function LoginPage() {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const session = useSession();
  const signIn = useSignIn();
  const change = useChangePassword();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [lockout, setLockout] = useState<number>(0);

  const mustChange = !!session.data?.user.mustChangePassword;
  const screen: 'signin' | 'change' = mustChange ? 'change' : 'signin';

  useEffect(() => {
    if (session.data && !mustChange) navigate('/', { replace: true });
  }, [session.data, mustChange, navigate]);
  useEffect(() => {
    if (!lockout) return;
    const id = setInterval(() => setLockout((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [lockout]);

  const rules = PASSWORD_RULES.map((r) => ({ key: r.key, ok: r.test(newPassword) }));
  const allRulesOk = rules.every((r) => r.ok);
  const mismatch = confirm.length > 0 && confirm !== newPassword;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (screen === 'signin') await signIn.mutateAsync({ email: email.trim().toLowerCase(), password });
      else {
        if (!allRulesOk || mismatch) return;
        await change.mutateAsync({ currentPassword: password, newPassword });
        navigate('/', { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 429 || err.code === 'too_many_requests')) setLockout(err.retryAfter ?? 30);
    }
  };
  const error = (signIn.error ?? change.error) as ApiError | null;
  // Only a refused credential says "wrong email or password". A 500, a 503 or a dropped connection is
  // not the reader's mistake, and telling them it is sends them round the loop retyping a correct password.
  const refused = error?.code === 'invalid_credentials' || error?.code === 'password_incorrect' || error?.status === 401;
  const locked = error?.status === 429 || error?.code === 'too_many_requests';
  const invalid = !!error && !locked;

  return (
    <div className="min-h-dvh grid lg:grid-cols-[1.15fr_1fr] bg-bg text-fg">
      <div className="flex flex-col justify-center px-6 py-14 sm:px-12 lg:px-[88px] lg:py-[72px]">
        <div className="w-full max-w-[430px] mx-auto lg:mx-0">
          <h1 className="text-[40px] font-light leading-[1.2] mb-3">{screen === 'signin' ? t('login.title') : t('login.changeTitle')}</h1>
          <p className="text-[17px] leading-relaxed text-muted mb-8">{screen === 'signin' ? t('login.subtitle') : t('login.changeSubtitle')}</p>

          {screen === 'change' ? <Notice tone="success" className="mb-7">{t('login.changeNotice')}</Notice> : null}
          {lockout > 0 ? (
            <Notice tone="warning" title={t('login.lockoutTitle')} className="mb-6">
              {t('login.lockoutBody', { seconds: lockout })}
            </Notice>
          ) : null}
          {invalid ? <Notice tone="error" className="mb-6">{refused ? (screen === 'signin' ? t('login.invalid') : t('errors.password_incorrect')) : errorText(error, t)}</Notice> : null}

          <form onSubmit={onSubmit} className="flex flex-col gap-6" noValidate>
            {screen === 'signin' ? (
              <Field label={t('login.email')} htmlFor="email">
                <Input id="email" type="email" autoComplete="username" inputMode="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" required />
              </Field>
            ) : null}
            <Field
              label={screen === 'signin' ? t('login.password') : t('login.currentPassword')}
              htmlFor="password"
              action={
                <button type="button" className="text-[13px] font-medium text-accent hover:text-fg" onClick={() => setReveal((v) => !v)}>
                  {reveal ? t('login.hide') : t('login.show')}
                </button>
              }
            >
              <Input id="password" type={reveal ? 'text' : 'password'} autoComplete="current-password" dir="ltr" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </Field>
            {screen === 'change' ? (
              <>
                <Field label={t('login.newPassword')} htmlFor="new">
                  <Input id="new" type={reveal ? 'text' : 'password'} autoComplete="new-password" dir="ltr" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('login.rules.length')} />
                </Field>
                <Field label={t('login.confirmPassword')} htmlFor="confirm" error={mismatch ? t('login.mismatch') : undefined}>
                  <Input id="confirm" type={reveal ? 'text' : 'password'} autoComplete="new-password" dir="ltr" value={confirm} onChange={(e) => setConfirm(e.target.value)} invalid={mismatch} />
                </Field>
                <div className="pt-1">
                  <p className="label mb-3.5">{t('login.rulesTitle')}</p>
                  <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
                    {rules.map((r) => (
                      <li key={r.key} className="flex items-center gap-2.5 text-sm">
                        <span className={`size-4 rounded-sm flex items-center justify-center text-[11px] leading-none ${r.ok ? 'bg-accent border border-accent text-accent-fg' : 'border border-input-line'}`} aria-hidden>
                          {r.ok ? '✓' : ''}
                        </span>
                        <span className={r.ok ? 'text-fg-2' : 'text-muted'}>{t(`login.rules.${r.key}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : null}
            <Button type="submit" className="w-full mt-2 min-h-14 text-base" loading={signIn.isPending || change.isPending} disabled={lockout > 0 || (screen === 'change' && (!allRulesOk || mismatch))}>
              {screen === 'signin' ? t('login.submit') : t('login.submitChange')}
            </Button>
          </form>

          <div className="mt-13 flex items-center justify-between gap-5">
            <button type="button" className="text-sm font-medium text-muted hover:text-fg" onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}>
              {t('app.language')}
            </button>
            <span className="text-[13px] text-muted">{t('login.footer')}</span>
          </div>
        </div>
      </div>

      <aside className="hidden lg:flex relative overflow-hidden flex-col justify-between bg-ink text-paper px-16 py-[72px]" style={{ colorScheme: 'dark' }}>
        <div className="absolute inset-0 pointer-events-none opacity-[0.12] bg-no-repeat" style={{ backgroundImage: 'url(/brand/wasity-mark.svg)', backgroundSize: 'auto 150%', backgroundPosition: 'right -260px center' }} aria-hidden />
        <img src="/brand/wasity-logo-horizontal-mint.svg" alt="" className="relative h-[46px] w-auto self-start" draggable={false} />
        <div className="relative max-w-[34ch]">
          <h2 className="text-[34px] font-light leading-[1.3] mb-4 text-paper">{t('app.panel')}</h2>
          <p className="text-[17px] leading-[1.7] text-paper/75">{t('app.tagline')}</p>
        </div>
        <span />
      </aside>
    </div>
  );
}
